/**
 * ideas.ts — contract schemas for the admin ideas surface (`/v1/admin/ideas`).
 *
 * The rows are a NORMALIZED projection of ideahub's idea view, not a verbatim
 * passthrough: the api flattens the blocker overlay (`state.blocked` +
 * `state.blocked_reason` / `blocked_owner`, timestamped by `state_ts.blocked`)
 * into explicit top-level fields so the SPA never has to know that the blocker
 * is an overlay stored inside a free-form state bag.
 *
 * `status` is deliberately a plain string, NOT an enum: group headers render
 * the status literal verbatim, and a literal the UI has never seen must still
 * render rather than fail validation. The display ORDER is hardcoded on the
 * client (admin/src/lib/ideas/status-groups.ts); unknown literals sort last.
 *
 * Source: idea 2026-08-18-ideas-widget-v3-status-groups (wandero slice).
 */

import { z } from 'zod';

// ── Idea row ─────────────────────────────────────────────────────────────────

export const AdminIdeaRowSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  /** Lifecycle status literal, or null for an idea that never got one. */
  status: z.string().nullable(),
  status_since: z.string().nullable(),
  last_activity_at: z.string().nullable(),
  created_at: z.string(),
  event_count: z.number(),
  /** Blocker OVERLAY — orthogonal to `status`, which keeps its own value. */
  blocked: z.boolean(),
  blocked_reason: z.string().nullable(),
  blocked_owner: z.string().nullable(),
  blocked_since: z.string().nullable(),
});

export type AdminIdeaRow = z.infer<typeof AdminIdeaRowSchema>;

export const AdminIdeasResponseSchema = z.object({
  ideas: z.array(AdminIdeaRowSchema),
});

export type AdminIdeasResponse = z.infer<typeof AdminIdeasResponseSchema>;

// ── Timeline event row ───────────────────────────────────────────────────────

/**
 * One ideahub event. `data` is the raw event body — an open facet bag
 * (`text` / `state` / `label` / `link`), so it stays untyped on purpose;
 * the drawer renders the facets it recognises and shows the rest as JSON.
 */
export const AdminIdeaEventSchema = z.object({
  id: z.number(),
  seq: z.number(),
  actor: z.string(),
  ts: z.string(),
  data: z.record(z.string(), z.unknown()),
});

export type AdminIdeaEvent = z.infer<typeof AdminIdeaEventSchema>;

export const AdminIdeaEventsResponseSchema = z.object({
  events: z.array(AdminIdeaEventSchema),
});

export type AdminIdeaEventsResponse = z.infer<typeof AdminIdeaEventsResponseSchema>;
